import { get, isFunction, map, noop, size } from 'lodash'
import React, { useState } from 'react'
import {
  AutoSizer,
  Column as VirtualizedColumn,
  Index,
  Table as VirtualizedTable,
  TableProps,
} from 'react-virtualized'

import Alert from './../Alert'
import HeaderRow from './HeaderRow'
import Row from './Row'
import { ITableColumn } from './types'

export interface TTable extends Partial<TableProps> {
  headerHeight?: number
  rowCount?: number
  hideHeader?: boolean
  children?: React.ReactNode
  setRef?: (r: VirtualizedTable) => void
}

const Table = (props: TTable): React.ReactElement => {
  const {
    columns = [],
    data = [],
    headerHeight = 30,
    rowHeight = 50,
    rowCount,
    scrollToAlignment = 'start',
    onRowsRendered = noop,
    rowGetter = ({ index }) => data[index],
    noRowsRenderer,
    hideHeader,
    rowStyle = {},
    children,
    ...restTable
  } = props

  const [ref, setRef] = useState<VirtualizedTable>()

  const onRef = (r: VirtualizedTable): void => {
    setRef(r)
    if (props.setRef) {
      props.setRef(r)
    }
  }

  const handleNoRowsRenderer = noRowsRenderer
    ? noRowsRenderer
    : (): React.ReactElement => {
        return (
          <div className="d-flex justify-content-center">
            <Alert variant="info" style={{ marginTop: 80 }} dismissible={false}>
              {i18n.t('table_no_results', 'Keine Ergebnisse verfügbar')}
            </Alert>
          </div>
        )
      }

  return (
    <AutoSizer>
      {({ width, height }) => (
        <VirtualizedTable
          ref={onRef}
          width={width || 100} // for functional on test
          height={height || 100} // for functional on test
          headerHeight={headerHeight}
          rowCount={rowCount || size(data)}
          rowHeight={rowHeight}
          scrollToAlignment="start"
          onRowsRendered={onRowsRendered}
          rowGetter={rowGetter}
          noRowsRenderer={handleNoRowsRenderer}
          headerRowRenderer={hideHeader ? () => null : HeaderRow}
          rowRenderer={(rowProps) => {
            const rowData = get(rowProps, 'rowData', {})
            const { isGroup, popover, groupRenderer } = rowData
            let rowColumns = rowProps.columns
            if (isGroup) {
              rowColumns = isFunction(groupRenderer)
                ? groupRenderer(rowData)
                : groupRenderer
            }
            const level = rowData.level || 0

            const index = rowProps.index as unknown as Index
            return (
              <Row
                {...rowProps}
                columns={rowColumns}
                level={level}
                rowStyle={isFunction(rowStyle) ? rowStyle(index) : rowStyle}
                popover={
                  popover && isFunction(popover) ? popover(rowData) : popover
                }
              />
            )
          }}
          {...restTable}
        >
          {children ||
            map(columns, (column, index) => {
              const { key, label, ...restColumn } = column
              return (
                <VirtualizedColumn
                  key={key}
                  width={100}
                  flexGrow={1}
                  headerRenderer={(headerProps) =>
                    headerHeight === 0 ? (
                      <React.Fragment />
                    ) : (
                      <div {...headerProps}>{label}</div>
                    )
                  }
                  cellRenderer={(cellProps) =>
                    props.cellRenderer ? (
                      props.cellRenderer(cellProps)
                    ) : (
                      <div {...cellProps}>
                        <span>{get(cellProps, 'cellData')}</span>
                      </div>
                    )
                  }
                  cellDataGetter={({ rowData }) => get(rowData, key)}
                  {...restColumn}
                />
              )
            })}
        </VirtualizedTable>
      )}
    </AutoSizer>
  )
}

export default Table

export { default as HeaderRow } from './HeaderRow'
export { default as Row } from './Row'
